/**
 * @file Параметры и типы для Field0
 * @module membrane/src/visual/field0/field0Params
 * 
 * Определяет типы данных и параметры для визуального слоя 0.
 */

/**
 * Параметры поля из Mirror API
 */
export interface Field0Params {
  seed: number;
  energy: number;
  turbulence: number;
  cohesion: number;
  drift: number;
  palette: string[]; // Hex цвета
  layers: Array<{
    scale: number;
    speed: number;
    opacity: number;
  }>;
}

/**
 * Uniforms для WebGL shader
 */
export interface Field0Uniforms {
  u_time: number;
  u_resolution: [number, number];
  u_mouse: [number, number];
  u_zoom: number;
  u_energy: number;
  u_turbulence: number;
  u_cohesion: number;
  u_drift: number;
  u_palette: Float32Array; // 4 цвета по 4 компонента (RGBA)
  u_layers: Float32Array; // 3 слоя по 3 параметра (scale, speed, opacity)
}

/**
 * Дефолтные параметры поля
 * 
 * Палитра: легкие воздушные облака - светлые, почти белые с легким теплом
 * Слои: очень медленное неспешное движение, воздушная прозрачность
 */
/**
 * Дефолтные параметры для первого уровня (стартовый экран)
 */
export const DEFAULT_FIELD_PARAMS: Field0Params = {
  seed: 12345,
  energy: 0.64, // Интенсивность света
  turbulence: 0.20, // Турбулентность
  cohesion: 0.40, // Плотность облаков
  drift: 0.05, // Скорость движения
  // Дефолтные цвета облаков для 0-го слоя
  palette: ['#63C0EA', '#8CC1FF', '#F1FAFF', '#86E5FF'], // Цвета 1-4
  layers: [
    // Слой 1 - большой масштаб
    { scale: 9.60, speed: 0.35, opacity: 0.20 },
    // Слой 2 - средний масштаб
    { scale: 7.00, speed: 0.50, opacity: 0.30 },
    // Слой 3 - малый масштаб
    { scale: 2.50, speed: 0.55, opacity: 0.35 },
  ],
};

/**
 * Параметры для Layer0Crystallization (после первого клика)
 */
export const CRYSTALLIZATION_FIELD_PARAMS: Field0Params = {
  seed: 12345,
  energy: 0.63, // Интенсивность света
  turbulence: 0.20, // Турбулентность
  cohesion: 0.40, // Плотность облаков
  drift: 0.05, // Скорость движения
  // Базовые цвета облаков (после применения насыщенности)
  palette: ['#FEFFFF', '#FCFEF7', '#CAF3FF', '#F9FFC6'], // Цвета 1-4
  layers: [
    // Слой 1 - большой масштаб
    { scale: 14.40, speed: 0.35, opacity: 0.20 },
    // Слой 2 - средний масштаб
    { scale: 9.70, speed: 0.50, opacity: 0.30 },
    // Слой 3 - малый масштаб
    { scale: 2.50, speed: 0.55, opacity: 0.35 },
  ],
};

/**
 * Преобразует hex цвет в массив [r, g, b, a]
 */
export function hexToRgba(hex: string): [number, number, number, number] {
  const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);
  if (!result) {
    return [0.95, 0.98, 1.0, 1.0]; // Дефолтный цвет - светло-голубой
  }
  
  return [
    parseInt(result[1], 16) / 255,
    parseInt(result[2], 16) / 255,
    parseInt(result[3], 16) / 255,
    1.0,
  ];
}

/**
 * Преобразует параметры поля в uniforms для shader
 */
export function paramsToUniforms(
  params: Field0Params,
  time: number,
  resolution: [number, number],
  mouse: [number, number],
  zoom: number
): Field0Uniforms {
  // Преобразуем палитру в Float32Array (4 цвета × 4 компонента)
  const paletteArray = new Float32Array(16);
  params.palette.forEach((hex, i) => {
    const rgba = hexToRgba(hex);
    paletteArray[i * 4 + 0] = rgba[0];
    paletteArray[i * 4 + 1] = rgba[1];
    paletteArray[i * 4 + 2] = rgba[2];
    paletteArray[i * 4 + 3] = rgba[3];
  });
  
  // Преобразуем слои в Float32Array (3 слоя × 3 параметра)
  const layersArray = new Float32Array(9);
  params.layers.forEach((layer, i) => {
    layersArray[i * 3 + 0] = layer.scale;
    layersArray[i * 3 + 1] = layer.speed;
    layersArray[i * 3 + 2] = layer.opacity;
  });
  
  return {
    u_time: time,
    u_resolution: resolution,
    u_mouse: mouse,
    u_zoom: zoom,
    u_energy: params.energy,
    u_turbulence: params.turbulence,
    u_cohesion: params.cohesion,
    u_drift: params.drift,
    u_palette: paletteArray,
    u_layers: layersArray,
  };
}

